import React, { useState } from "react";
import { usePaymentMethods } from "../../contexts/PaymentMethodsContext";
import { Button } from "../ui/Button";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { 
  CreditCard, 
  Building2, 
  MoreHorizontal, 
  Trash2, 
  Star,
  StarOff,
  AlertCircle
} from "lucide-react";

const PaymentMethodsList: React.FC = () => {
  const { paymentMethods, deletePaymentMethod, setDefaultPaymentMethod, loading } = usePaymentMethods();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this payment method?")) {
      try {
        setDeletingId(id);
        await deletePaymentMethod(id);
      } catch (error) {
        alert("Failed to delete payment method");
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      setSettingDefaultId(id);
      await setDefaultPaymentMethod(id);
    } catch (error) {
      alert("Failed to set default payment method");
    } finally {
      setSettingDefaultId(null);
    }
  };

  // Filter out PayPal methods (they are for withdrawals only, not payments)
  const nonPayPalMethods = paymentMethods.filter(method => method.type !== "paypal");

  if (loading) {
    return (
      <div className="flex flex-col sm:flex-row items-center justify-center p-6 sm:p-8">
        <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600"></div>
        <span className="ml-0 sm:ml-3 mt-2 sm:mt-0 text-sm sm:text-base text-gray-600">Loading payment methods...</span>
      </div>
    );
  }

  if (nonPayPalMethods.length === 0) {
    return (
      <div className="text-center p-6 sm:p-8">
        <CreditCard className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No Payment Methods</h3>
        <p className="text-sm sm:text-base text-gray-600 px-4">You haven&apos;t added any payment methods yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {nonPayPalMethods.filter(method => method.type === "card").map((method) => (
        <Card key={method.id} className={`p-4 sm:p-6 relative ${
          method.isDefault ? "border-blue-200 bg-blue-50" : 
          ((method.type as string) === "us_bank_account" || method.type === "bank_account") && Number(method.status) === 2 ? "border-yellow-200 bg-yellow-50" :
          ""
        }`}>
          {/* Action buttons positioned at top right on mobile */}
          <div className="absolute top-3 right-3 sm:hidden flex items-center gap-1">
            {/* Star button for setting default */}
            {!method.isDefault && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSetDefault(method.id)}
                disabled={settingDefaultId === method.id}
                className="text-blue-600 hover:text-blue-800 p-1 h-9 w-9"
                title="Set as default"
              >
                {settingDefaultId === method.id ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                ) : (
                  <Star className="h-5 w-5" />
                )}
              </Button>
            )}
            
            {/* Delete button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(method.id)}
              disabled={deletingId === method.id}
              className="text-red-600 hover:text-red-800 p-1 h-9 w-9"
              title="Delete payment method"
            >
              {deletingId === method.id ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
              ) : (
                <Trash2 className="h-5 w-5" />
              )}
            </Button>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              {method.type === "card" ? (
                <CreditCard className="h-6 w-6 sm:h-8 sm:w-8 text-gray-600 flex-shrink-0 mt-1" />
              ) : (method.type as string) === "us_bank_account" || method.type === "bank_account" ? (
                <Building2 className="h-6 w-6 sm:h-8 sm:w-8 text-gray-600 flex-shrink-0 mt-1" />
              ) : (
                <CreditCard className="h-6 w-6 sm:h-8 sm:w-8 text-gray-600 flex-shrink-0 mt-1" />
              )}
              
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <h4 className="text-sm sm:text-base font-medium text-gray-900 truncate">
                    {method.type === "card" && `${method.brand?.toUpperCase()} ****${method.last4}`}
                    {(method.type as string) === "us_bank_account" && `${method.bankName} ****${method.last4}`}
                    {method.type === "bank_account" && `${method.bankName} ****${method.last4}`}
                  </h4>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-gray-100 text-gray-700 text-xs px-2 py-1" variant={undefined}>
                      {method.type === "card" && "Card"}
                      {(method.type as string) === "us_bank_account" && "Bank Account"}
                      {method.type === "bank_account" && "Bank Account"}
                    </Badge>
                    {method.isDefault && (
                      <Badge className="bg-blue-100 text-blue-800 text-xs px-2 py-1" variant={undefined}>
                        Default
                      </Badge>
                    )}
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 mt-1 break-words">
                  {method.type === "card" && `Expires ${method.expMonth}/${method.expYear}`}
                  {(method.type as string) === "us_bank_account" && (
                    <span className="block sm:inline">
                      <span className="hidden sm:inline">{method.accountHolderName} • {method.routingNumber}</span>
                      <span className="sm:hidden">{method.accountHolderName}</span>
                    </span>
                  )}
                  {method.type === "bank_account" && (
                    <span className="block sm:inline">
                      <span className="hidden sm:inline">{method.accountHolderName} • {method.routingNumber}</span>
                      <span className="sm:hidden">{method.accountHolderName}</span>
                    </span>
                  )}
                </p>
                {((method.type as string) === "us_bank_account" || method.type === "bank_account") && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      Number(method.status) === 1 ? "bg-green-100 text-green-800" :
                      Number(method.status) === 2 ? "bg-yellow-100 text-yellow-800" :
                      Number(method.status) === 3 ? "bg-blue-100 text-blue-800" :
                      "bg-gray-100 text-gray-800"
                    }`}>
                      {Number(method.status) === 1 ? "Verified" :
                       Number(method.status) === 2 ? "Pending Verification" :
                       Number(method.status) === 3 ? "Verified" :
                       "Unknown"}
                    </span>
                    {Number(method.status) === 2 && (
                      <span className="text-xs text-yellow-600 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3 flex-shrink-0" />
                        <span className="hidden sm:inline">Verification required</span>
                        <span className="sm:hidden">Verify required</span>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2">
              {/* Verification button for bank accounts that need verification */}
              {((method.type as string) === "us_bank_account" || method.type === "bank_account") && Number(method.status) === 2 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Handle verification - you can implement verification flow here
                    alert("Verification process would be initiated here. This would typically redirect to Stripe's verification flow.");
                  }}
                  className="text-yellow-600 border-yellow-300 hover:bg-yellow-50 text-xs sm:text-sm px-3 py-2 w-full sm:w-auto"
                >
                  <AlertCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Verify</span>
                  <span className="sm:hidden">Verify Account</span>
                </Button>
              )}
              
              <div className="flex items-center gap-2">
                {!method.isDefault && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSetDefault(method.id)}
                    disabled={settingDefaultId === method.id}
                    className="text-blue-600 hover:text-blue-800 p-2 hidden sm:flex"
                    title="Set as default"
                  >
                    {settingDefaultId === method.id ? (
                      <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-blue-600"></div>
                    ) : (
                      <Star className="h-3 w-3 sm:h-4 sm:w-4" />
                    )}
                  </Button>
                )}
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(method.id)}
                  disabled={deletingId === method.id}
                  className="text-red-600 hover:text-red-800 p-2 hidden sm:flex"
                  title="Delete payment method"
                >
                  {deletingId === method.id ? (
                    <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-red-600"></div>
                  ) : (
                    <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default PaymentMethodsList;
